import React, { useState } from "react";
import { Menu, X, Instagram, Youtube, MessageCircle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import logoImage from "../assets/logo.png";
import { getEffectiveFeatureFlags } from "../utils/featureFlags";
import { StoreInfo } from "../types";

interface HeaderProps {
  storeInfo: StoreInfo;
}

export const Header: React.FC<HeaderProps> = ({ storeInfo }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Get feature flags
  const featureFlags = getEffectiveFeatureFlags();

  // Build navigation items based on feature flags
  const navigationItems = [
    { label: "Home", href: "/" },
    { label: "Categories", href: "/categories" },
    ...(featureFlags.showProducts
      ? [{ label: "All Products", href: "/products" }]
      : []),
    ...(featureFlags.showOffers ? [{ label: "Offers", href: "/offers" }] : []),
    ...(featureFlags.showVideos ? [{ label: "Videos", href: "/videos" }] : []),
    { label: "Contact", href: "/contact" },
  ];

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0">
              <img
                src={logoImage}
                alt="Benipal Toys & Gift Store"
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navigationItems.map((item) => {
              const isActive =
                location.pathname === item.href ||
                (item.href !== "/" && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`px-3 py-2 text-sm font-medium transition-colors duration-200 hover:scale-105 transform ${
                    isActive
                      ? "text-purple-600 border-b-2 border-purple-600 font-semibold"
                      : "text-gray-700 hover:text-purple-600"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Social Media Follow Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <a
              href={storeInfo.socialMedia.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-600 hover:text-pink-600 transition-colors duration-200 hover:scale-110 transform"
              title="Follow on Instagram"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href={storeInfo.socialMedia.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-600 hover:text-red-600 transition-colors duration-200 hover:scale-110 transform"
              title="Subscribe on YouTube"
            >
              <Youtube className="h-5 w-5" />
            </a>
            <a
              href={storeInfo.socialMedia.whatsappChannel}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-600 hover:text-green-600 transition-colors duration-200 hover:scale-110 transform"
              title="Join WhatsApp Updates"
            >
              <MessageCircle className="h-5 w-5" />
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center">
            <button
              className="md:hidden p-2 text-gray-700 hover:text-purple-600 transition-colors duration-200"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white rounded-lg shadow-lg mt-2">
              {navigationItems.map((item) => {
                const isActive =
                  location.pathname === item.href ||
                  (item.href !== "/" &&
                    location.pathname.startsWith(item.href));
                return (
                  <Link
                    key={item.label}
                    to={item.href}
                    className={`block px-3 py-2 text-base font-medium transition-colors duration-200 hover:scale-105 transform ${
                      isActive
                        ? "text-purple-600 bg-purple-50 rounded-lg font-semibold"
                        : "text-gray-700 hover:text-purple-600"
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                );
              })}

              {/* Mobile Social Media Buttons */}
              <div className="flex items-center justify-center space-x-4 pt-4 border-t border-gray-200">
                <a
                  href={storeInfo.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 text-gray-600 hover:text-pink-600 transition-colors duration-200 hover:scale-110 transform"
                  title="Follow on Instagram"
                >
                  <Instagram className="h-6 w-6" />
                </a>
                <a
                  href={storeInfo.socialMedia.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 text-gray-600 hover:text-red-600 transition-colors duration-200 hover:scale-110 transform"
                  title="Subscribe on YouTube"
                >
                  <Youtube className="h-6 w-6" />
                </a>
                <a
                  href={storeInfo.socialMedia.whatsappChannel}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 text-gray-600 hover:text-green-600 transition-colors duration-200 hover:scale-110 transform"
                  title="Join WhatsApp Updates"
                >
                  <MessageCircle className="h-6 w-6" />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
